// Presentation only: never mutate the saved AI response or infer new issues.
export const splitPoint2DataQuality = (content) => {
  const unchanged = { analysis: content, warning: '', start: -1, end: -1 };
  if (typeof content !== 'string') return unchanged;
  // Match heading structure while ignoring inline formatting, without changing
  // original offsets/text needed by display and edit round-tripping.
  const inline = String.raw`(?:[*_\x60~]+|<\/?(?:strong|b|em|i|u|span|mark|code)\b[^>]*>)`;
  const formatting = `(?:[ \\t\\u00a0]|&nbsp;|${inline})*`;
  const gap = `${inline}*(?:[ \\t\\u00a0]|&nbsp;)+${formatting}`;
  const opening = `[ \\t]*(?:<(?:p|div|h[1-6])\\b[^>]*>[ \\t]*)?(?:#{1,6}[ \\t]+)?${formatting}`;
  const closing = `${formatting}:?${formatting}(?:<\\/(?:p|div|h[1-6])>)?`;
  const note = 'NOT(?:ES?|ICES?)';
  const label = `DATA${gap}(?:COMPLETENESS${gap}${note}|QUALITY${gap}(?:(?:(?:AND|&(?:amp;)?)${gap}COMPLETENESS${gap})?${note}|ISSUES${gap}DETECTED))`;
  const parenthetical = `(?:${formatting}\\(${formatting}read${gap}before${gap}interpreting${formatting}\\))?`;
  const marker = new RegExp(`(?:^|(?<=>))${opening}(?:⚠\\uFE0F?[ \\t]*)?(${label})${parenthetical}(?=${formatting}(?::|<\\/|\\r?$))${closing}`, 'gim');
  // Only actual orientation headings delimit the block, never prose mentioning one.
  const boundary = new RegExp(`(?:^|(?<=>))${opening}(?:CLIENT['’]S[ \\t]+DOMINATING[ \\t]+ORIENTATION[ \\t]+ANALYSIS|[A-Za-z][A-Za-z -]*[ \\t]+Orientation|INTEGRATED[ \\t]+ORIENTATION[ \\t]+PROFILE)(?:${formatting}[—–-][ \\t]*heuristic[ \\t]+estimate[^\\r\\n<]*)?[ \\t]*:?${closing}[ \\t]*(?=\\r?$|<)`, 'im');
  const blocks = [];
  let match;
  while ((match = marker.exec(content))) {
    // A heading may start a text line or HTML block, not a bold phrase mid-paragraph.
    const preceding = content.slice(0, match.index);
    if (match.index && !/[\r\n]$/.test(preceding)
        && !/<\/?(?:p|div|h[1-6]|br)\b[^>]*>$/i.test(preceding)) continue;
    const tailStart = match.index + match[0].length;
    const tail = content.slice(tailStart);
    const nextHeading = boundary.exec(tail);
    const normalizedLabel = match[1].replace(/<[^>]*>|[*_`~]/g, '').replace(/&nbsp;/gi, ' ').replace(/\s+/g, ' ').toUpperCase();
    const completeness = /\bNOT(?:ES?|ICES?)$/.test(normalizedLabel);
    let end = nextHeading ? tailStart + nextHeading.index : content.length;
    if (completeness) {
      // A peer/parent heading ends the subsection; deeper note headings stay in it.
      const level = Number(match[0].match(/<h([1-6])\b/i)?.[1])
        || match[0].match(/#{1,6}(?=\s)/)?.[0].length || 6;
      const sections = /(?:^|(?<=>))[ \t]*(?:(#{1,6})[ \t]+([^\r\n]+)|<h([1-6])\b[^>]*>(.*?)<\/h\3>)/gim;
      let section;
      while ((section = sections.exec(tail))) {
        const label = (section[2] || section[4]).replace(/<[^>]*>/g, '');
        const analysisHeading = /\b(?:orientation|interpretation|analysis|profile|summary|synthesis|conclusions?)\b/i.test(label)
          && !/\b(?:data quality|completeness|notes)\b/i.test(label);
        if (analysisHeading && (section[1]?.length || Number(section[3])) <= level) {
          end = Math.min(end, tailStart + section.index);
          break;
        }
      }
      // Explicit horizontal rules also close a block, including HTML-only reports.
      const dividers = /(?:^|(?<=>))[ \t]*(?:(?:-{3,}|\*{3,}|_{3,})[ \t]*(?:\r?\n|$)|<hr\b[^>]*>)[\s]*/gim;
      let divider;
      while ((divider = dividers.exec(tail))) {
        // A decorative rule immediately under the title does not end its notes.
        if (tail.slice(0, divider.index).replace(/<[^>]*>|[-*_\s]/g, '').length) {
          end = Math.min(end, tailStart + divider.index + divider[0].length);
          break;
        }
      }
    }
    let start = match.index;
    if (completeness) {
      // Include the subsection's leading rule/blank block in its removed range.
      const prefix = content.slice(0, start);
      const rule = /(?:^|\r?\n)[ \t]*(?:-{3,}|\*{3,}|_{3,}|<hr\b[^>]*>)[ \t]*(?:\r?\n[ \t]*)*$/i.exec(prefix);
      if (rule) start = rule.index + (rule[0].startsWith('\r\n') ? 2 : rule[0].startsWith('\n') ? 1 : 0);
      const blanks = /(?:\r?\n){3,}[ \t]*$/.exec(content.slice(0, start));
      if (blanks) start = blanks.index + (blanks[0].startsWith('\r\n') ? 4 : 2);
    }
    start = Math.max(start, blocks[blocks.length - 1]?.end || 0);
    blocks.push({ start, end, text: content.slice(start, end),
      notes: content.slice(tailStart, end).replace(new RegExp(marker.source, marker.flags), '') });
    marker.lastIndex = end;
  }
  if (!blocks.length) return unchanged;
  let cursor = 0;
  let analysis = '';
  blocks.forEach(block => {
    analysis += content.slice(cursor, block.start);
    cursor = block.end;
  });
  analysis += content.slice(cursor);
  return { analysis, warning: blocks.map(block => block.text).join(''), start: blocks[0].start, end: blocks[blocks.length - 1].end, blocks };
};

export const pointAnalysisDisplay = (point) => Number(point.point_number) === 2
  ? splitPoint2DataQuality(point.content).analysis : point.content;

// Keep each exact extracted block when an existing orientation analysis is edited.
export const replacePointAnalysis = (point, analysis) => {
  if (Number(point.point_number) !== 2) return analysis;
  const { blocks } = splitPoint2DataQuality(point.content);
  if (!blocks) return analysis;
  let removed = 0;
  let inserted = 0;
  let result = analysis;
  blocks.forEach(block => {
    const position = Math.min(block.start - removed, analysis.length) + inserted;
    result = result.slice(0, position) + block.text + result.slice(position);
    removed += block.text.length;
    inserted += block.text.length;
  });
  return result;
};

export const phase5DataQualityContent = (report = [], notesOnly = false) => {
  // Presence matters: an explicitly empty new output must suppress legacy notes.
  if (!Array.isArray(report) && typeof report?.data_quality_note === 'string') {
    return humanizeDataQualityNote(report.data_quality_note);
  }
  const points = Array.isArray(report) ? report : (report?.synthesis_points || report?.points || []);
  const extracted = splitPoint2DataQuality(points.find(point => Number(point.point_number) === 2)?.content || '');
  return notesOnly ? (extracted.blocks || []).map(block => block.notes).join('\n\n') : extracted.warning;
};

const DATA_QUALITY_PHASES = {
  'phase 1': 'Phase 1 — Client Contextual Demographics',
  'phase 2': 'Phase 2 — Client Moral Reflections',
  'phase 3': 'Phase 3 — Client Views',
  'phase 4': 'Phase 4 — Client Aspirations',
};
const DATA_QUALITY_GROUPS = [
  ['Missing or Incomplete Responses', /missing|incomplete|blank|empty|absent|required|descriptor/i],
  ['Timeouts', /timed?[_ -]?out|timeout|time expired|expired/i],
  ['Unusable or Unclear Responses', /gibberish|unusable|unclear|illegible|uninterpretable/i],
  ['Internal Inconsistencies', /does not match|mismatch|contradict|inconsistent|total_associations|count/i],
  ['Categorization or Suggestion Issues', /ai[_ -]?suggest|categor(?:y|ization)|classif/i],
  ['Interpretation Limitations', /interpret|limited|limitation|not available|not enough/i],
];

const humanPhase = (line) => line.replace(/\bPhase\s*([1-4])\b/gi, (_, n) => DATA_QUALITY_PHASES[`phase ${n}`]);
const readableItem = (line) => line
  .replace(/\b(?:word_associations|favorite_things)\[\d+\]/gi, '')
  .replace(/\b(?:associations|classifications|ai_suggestions|system_states|thematic_domains|words)\[\d+\]/gi, '')
  .replace(/\b(?:required_descriptor_count|descriptors?|timed_out|system_state|target_text|reasoning|is_gibberish|ai_error|metadata)\s*[:=]\s*[^;,\n]+/gi, '')
  .replace(/\b(?:Phase\s*[1-4]\.)[A-Za-z0-9_.\[\]-]+/gi, (match) => match.replace(/\.$/, ''))
  .replace(/\s*\[[^\]]*\]\s*/g, ' ')
  .replace(/\s{2,}/g, ' ')
  .replace(/\s+([,.;:])/g, '$1')
  .trim();

const humanizeTechnicalLine = (line) => {
  const phase = (n) => DATA_QUALITY_PHASES[`phase ${n}`];
  let match = line.match(/Phase\s*3[^\n]*?(?:['“"]([^'”"]+)['”"])?[^\n]*?(?:timed[_ -]?out|time(?:d)?\s+expired)\s*[:=]?\s*(?:true|yes)?/i);
  if (match) return `${phase(3)}: One response${match[1] ? ` to “${match[1]}”` : ''} timed out and was not available for interpretation.`;
  match = line.match(/Phase\s*4[^\n]*?(?:['“"]([^'”"]+)['”"])?[^\n]*?required[_ ]descriptor[_ ]count\s*[:=]\s*(\d+)[^\n]*?(?:descriptors?|available)\s*[:=]\s*(\d+)/i);
  if (match) return `${phase(4)}: ${match[1] ? `“${match[1]}” ` : 'A favourite item '}${`contains ${match[3]} of the ${match[2]} required descriptions, so interpretation is limited.`}`;
  match = line.match(/Phase\s*4[^\n]*?(?:['“"]([^'”"]+)['”"])?[^\n]*?(?:ai[_ -]?suggestions?|categorization)[^\n]*?(?:gibberish|unusable|unclear)/i);
  if (match) return `${phase(4)}: An AI categorization suggestion${match[1] ? ` for “${match[1]}”` : ''} contained unusable text and was excluded.`;
  match = line.match(/Phase\s*3[^\n]*total[_ ]associations\s*[:=]\s*(\d+)[^\n]*(?:enumerated|available)[^\d]*(\d+)/i);
  if (match) return `${phase(3)}: The recorded total does not match the number of available responses. Interpretation used the responses that were actually present.`;
  if (/Phase\s*4[^\n]*(?:favorite(?:_| )things|favourite item)[^\n]*index\s*\d+[^\n]*entirely timed out/i.test(line)) {
    return `${phase(4)}: A favourite item was entirely timed out and had no usable content.`;
  }
  match = line.match(/Phase\s*4[^\n]*(?:favorite(?:_| )things|favourite item)[^\n]*index\s*\d+[^\n]*\((?:['“"])?([^'”")]+)(?:['”"])?\)[^\n]*?(?:only|records)\s+(\d+)\s+(?:of|descriptor)/i);
  if (match) return `${phase(4)}: “${match[1]}” contains only ${match[2]} usable description${match[2] === '1' ? '' : 's'}, so interpretation is limited.`;
  if (/Phase\s*4[^\n]*(?:favorite(?:_| )things|favourite item)[^\n]*index\s*\d+[^\n]*required descriptor/i.test(line)) {
    return `${phase(4)}: A favourite item has missing required descriptions, so interpretation is limited.`;
  }
  return null;
};

// Presentation-only compatibility for dedicated notes generated before the
// readable contract. It never mutates storage and leaves established prose
// and historical non-technical notes untouched.
export const humanizeDataQualityNote = (raw) => {
  if (typeof raw !== 'string' || !raw.trim()) return raw || '';
  const source = raw.replace(/<[^>]*>/g, '').replace(/```(?:markdown|json)?/gi, '').replace(/```/g, '');
  const rawLines = source.split(/\r?\n/).map(line => line.replace(/^\s*[-*]\s*/, '').trim()).filter(Boolean);
  const technical = rawLines.some(line => /(?:word_associations|favorite_things|ai_suggestions|required_descriptor_count|timed_out|system_state|target_text|reasoning|\[\d+\]|\[Phase\s*[1-4][^\]]*\])/i.test(line));
  const lines = rawLines.map(line => humanizeTechnicalLine(line) || readableItem(line)).map(line => line
    .replace(/Phase 1 — Client Contextual Demographics — Client Contextual Demographics:/gi, 'Phase 1 — Client Contextual Demographics:')
    .replace(/Phase 2 — Client Moral Reflections — Client Moral Reflections:/gi, 'Phase 2 — Client Moral Reflections:')
    .replace(/Phase 3 — Client Views — Client Views:/gi, 'Phase 3 — Client Views:')
    .replace(/Phase 4 — Client Aspirations — Client Aspirations:/gi, 'Phase 4 — Client Aspirations:')
    .replace(/\b(?:word_associations|favorite_things|ai_suggestions)\b/gi, '')
    .replace(/\b(?:field_timed_out|descriptor_timed_out|descriptor_system_states|field_system_states|system_states|timed_out|is_gibberish|required_descriptor_count|what_is_it|coping_mechanism|target_text|reasoning)\b/gi, '')
    .replace(/\b(?:system state|system states)\s+[^,;.]+/gi, '')
    .replace(/\b(?:word|item)\s+index\s*\d+/gi, 'item')
    .replace(/\s*\[[^\]]*\]\s*/g, ' ')
    .replace(/\s{2,}/g, ' ')
    .replace(/\s+([,.;:])/g, '$1')
    .trim());
  if (!technical) return raw;
  const groups = new Map();
  lines.forEach(line => {
    const phase = (line.match(/\bPhase\s*[1-4]\b/i) || [])[0]?.toLowerCase();
    let clean = /^Phase\s*[1-4]\s+—/i.test(line) ? line : humanPhase(line);
    clean = clean.replace(/\b(?:true|false)\b/gi, '').replace(/\s{2,}/g, ' ').trim();
    const group = DATA_QUALITY_GROUPS.find(([, pattern]) => pattern.test(clean))?.[0] || 'Interpretation Limitations';
    if (!groups.has(group)) groups.set(group, []);
    const bucket = groups.get(group);
    if (!bucket.some(item => item.toLowerCase() === clean.toLowerCase())) bucket.push(clean);
  });
  return [...groups].map(([heading, items]) => `**${heading}**\n${items.map(item => `- ${item}`).join('\n')}`).join('\n\n');
};
